import type { ReactNode } from 'react'

/**
 * On-brand CSS illustrations used in place of product screenshots for case
 * studies without shippable shots (1Mind, Peaque). They reuse the site's
 * design tokens and card vocabulary (panel surfaces, hair borders, mono
 * labels, amber/green accents) so they read as part of the same system.
 */

function Frame({ children, className = '' }: { children: ReactNode; className?: string }) {
  return <div className={`illus ${className}`.trim()}>{children}</div>
}

function Chrome({ label }: { label?: string }) {
  return (
    <div className="illus-bar">
      <span className="illus-dots"><i /><i /><i /></span>
      {label && <span className="illus-url">{label}</span>}
    </div>
  )
}

function Lines({ specs }: { specs: Array<string> }) {
  return (
    <>
      {specs.map((cls, i) => <div className={`illus-line ${cls}`} key={i} />)}
    </>
  )
}

/* ============================ 1Mind ============================ */

export function OneMindHero() {
  const bars = Array.from({ length: 30 }, (_, i) => 5 + Math.abs(Math.sin(i * 0.7)) * 21)
  return (
    <Frame className="illus-hero-card">
      <div className="illus-superhuman">
        <div className="illus-avatar lg"><span className="illus-pulse" /></div>
        <div className="illus-sh-body">
          <span className="illus-badge"><span className="dotlive" /> Superhuman · live</span>
          <div className="illus-bubble">Hi — what brings you to Acme today?</div>
          <div className="illus-wave">
            {bars.map((h, i) => <i style={{ height: `${h}px` }} key={i} />)}
          </div>
          <div className="illus-chips">
            <span className="chip key">Voice + avatar</span>
            <span className="chip">Real-time</span>
          </div>
        </div>
      </div>
    </Frame>
  )
}

export function OneMindSite() {
  return (
    <Frame>
      <Chrome label="acme.com" />
      <div className="illus-page">
        <Lines specs={['w70', 'w40', 'w90 soft', 'w60 soft', 'w80 soft']} />
      </div>
      <div className="illus-widget">
        <div className="illus-avatar sm" />
        <div className="illus-widget-body">
          <div className="illus-bubble sm">Looking for pricing? I can help →</div>
          <div className="illus-typing"><i /><i /><i /></div>
        </div>
      </div>
    </Frame>
  )
}

export function OneMindInProduct() {
  return (
    <Frame>
      <Chrome label="app · onboarding" />
      <div className="illus-app">
        <div className="illus-side"><i /><i className="on" /><i /><i /></div>
        <div className="illus-main">
          <Lines specs={['w50']} />
          <div className="illus-tip">
            <div className="illus-avatar xs" />
            <span>Connect your data to finish setup →</span>
          </div>
          <div className="illus-progress"><span style={{ width: '50%' }} /></div>
          <span className="illus-badge soft">Activation · 2 / 4</span>
        </div>
      </div>
    </Frame>
  )
}

export function OneMindCall() {
  return (
    <Frame>
      <div className="illus-callbar">
        <span className="rec"><i /> REC</span>
        <span className="illus-url">Zoom · live</span>
      </div>
      <div className="illus-grid">
        <div className="illus-tile"><span className="illus-face" /><b>AE · Sarah</b></div>
        <div className="illus-tile ai"><span className="illus-avatar sm" /><b>Superhuman · SE</b></div>
        <div className="illus-tile"><span className="illus-face" /><b>Buyer · VP</b></div>
        <div className="illus-tile"><span className="illus-face" /><b>Buyer · CTO</b></div>
      </div>
      <div className="illus-caption">Answering: “How do SSO &amp; SOC 2 work?”</div>
    </Frame>
  )
}

/* ============================ Peaque ============================ */

export function PeaqueHero() {
  return (
    <Frame className="illus-split illus-hero-card">
      <div className="illus-pagecol">
        <Lines specs={['w80', 'w95 soft', 'w90 soft hl', 'w70 soft', 'w85 soft', 'w60 soft']} />
      </div>
      <div className="illus-notecol">
        <div className="illus-note">
          <div className="illus-note-head"><span className="illus-avatar xs" /> @reader</div>
          <Lines specs={['w90 soft', 'w70 soft']} />
          <div className="illus-react"><span>▲ 248</span><span>↩ 12</span><span>↗</span></div>
        </div>
      </div>
    </Frame>
  )
}

export function PeaqueRead() {
  return (
    <Frame>
      <Chrome label="peaque · reader" />
      <div className="illus-reader">
        <div className="illus-pagecol">
          <Lines specs={['w70', 'w90 soft', 'w85 soft', 'w95 soft', 'w60 soft']} />
        </div>
        <div className="illus-notecol slim">
          <div className="illus-mininote" /><div className="illus-mininote" />
        </div>
      </div>
      <div className="illus-progress"><span style={{ width: '42%' }} /></div>
    </Frame>
  )
}

export function PeaqueCreate() {
  return (
    <Frame>
      <div className="illus-pagecol">
        <Lines specs={['w80 soft', 'w90 soft hl', 'w50 soft']} />
      </div>
      <div className="illus-popover">
        <div className="illus-pop-tools"><span>✎ Note</span><span>★</span><span>↗</span></div>
        <div className="illus-compose">
          <div className="illus-avatar xs" />
          <span>“This twist changes everything…”</span>
        </div>
        <div className="illus-pop-actions">
          <span className="illus-pill">Private</span>
          <span className="illus-pill on">Public</span>
        </div>
      </div>
    </Frame>
  )
}

export function PeaqueCelebrate() {
  const rows: Array<[string, string]> = [['@maya', '▲ 312'], ['@theorist', '▲ 187'], ['@artfan', '▲ 96']]
  return (
    <Frame>
      <div className="illus-notelist">
        {rows.map(([user, votes], i) => (
          <div className={`illus-note row${i === 0 ? ' top' : ''}`} key={user}>
            <span className="illus-avatar xs" />
            <div className="illus-note-main"><b>{user}</b><div className="illus-line w80 soft" /></div>
            <div className="illus-react"><span>{votes}</span><span>↩</span><span>↗</span></div>
          </div>
        ))}
      </div>
    </Frame>
  )
}

/* ===== Compact thumbnails for the landing "Featured work" list ===== */

export function OneMindThumb() {
  const bars = [8, 14, 10, 17, 11, 15, 9]
  return (
    <div className="illus-thumb">
      <span className="illus-avatar" />
      <span className="illus-thumb-wave">
        {bars.map((h, i) => <i style={{ height: `${h}px` }} key={i} />)}
      </span>
    </div>
  )
}

/**
 * A brand logo rendered as a single-colour mark in the company's own brand
 * colour (via CSS mask), sitting on the standard dark tile. This keeps each
 * brand's identity while blending with the dark design — no white plate.
 */
export function LogoThumb({ src, alt, color }: { src: string; alt: string; color: string }) {
  return (
    <div className="illus-thumb logo">
      <span
        className="logo-mark"
        role="img"
        aria-label={alt}
        style={{ ['--logo' as string]: `url("${src}")`, ['--logo-color' as string]: color }}
      />
    </div>
  )
}

export function PeaqueThumb() {
  return (
    <div className="illus-thumb peaque">
      <span className="illus-thumb-line" />
      <span className="illus-thumb-line hl" />
      <span className="illus-thumb-line s" />
    </div>
  )
}

export function PeaqueEngine() {
  return (
    <Frame>
      <div className="illus-pipe">
        <div className="illus-node"><span className="illus-node-ic">⤓</span>EPUB<small>.zip</small></div>
        <span className="illus-arrow">→</span>
        <div className="illus-node"><span className="illus-node-ic">{'{ }'}</span>Parse</div>
        <span className="illus-arrow">→</span>
        <div className="illus-node"><span className="illus-node-ic">▦</span>Render</div>
        <span className="illus-arrow">→</span>
        <div className="illus-node on"><span className="illus-node-ic">▤</span>Page</div>
      </div>
      <span className="illus-badge soft center">serverless · rendered on demand</span>
    </Frame>
  )
}
